/**
 * SuspenderSalidaDialog — cancelación masiva de una salida.
 *
 * Flujo:
 *   1. Confirma cuántas reservas activas hay
 *   2. Pide tipo (clima/DIMAR/operativo/otro) + motivo obligatorio
 *   3. Vigencia del crédito a generar (default 365 días)
 *   4. Confirma → backend marca todas las reservas como suspendidas + crea créditos
 *   5. Muestra resultado con descarga CSV de contactos para avisar manualmente
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, MenuItem, Button, Alert,
  CircularProgress, Chip, Divider, Paper,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SlideOver from '../common/SlideOver';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return localStorage.getItem('empresa_activa') || JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const TIPOS = [
  { v: 'clima',     label: '🌧️ Clima',          help: 'Suspensión por mal tiempo (lluvia, oleaje)' },
  { v: 'dimar',     label: '⚓ DIMAR',           help: 'Suspensión ordenada por Autoridad Marítima' },
  { v: 'operativo', label: '⚙️ Operativo',       help: 'Falla de bote, capitán, logística interna' },
  { v: 'otro',      label: '📝 Otro',            help: 'Especifica en el motivo' },
];

export default function SuspenderSalidaDialog({ salida, onClose, onSuspendida }) {
  const { toast, ToastContainer } = useToast();
  const [tipo, setTipo] = useState('clima');
  const [motivo, setMotivo] = useState('');
  const [diasVigencia, setDiasVigencia] = useState(365);
  const [reservasCount, setReservasCount] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Contar reservas activas de la salida
  useEffect(() => {
    fetch(`${API_BASE}/turismo/reservaciones?empresa_id=${getEmpresaId()}&salida_id=${salida.id}&limit=1000`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.json())
      .then(d => {
        const rs = d.reservaciones || d || [];
        const activas = rs.filter(r => ['pendiente', 'confirmada', 'hold'].includes(r.estado));
        setReservasCount({
          total: activas.length,
          totalPax: activas.reduce((acc, r) => acc + (r.total_pasajeros || 0), 0),
          totalMonto: activas.reduce((acc, r) => acc + (Number(r.precio_total) - Number(r.saldo_pendiente || 0)), 0),
        });
      })
      .catch(() => setReservasCount({ total: 0, totalPax: 0, totalMonto: 0 }));
  }, [salida.id]);

  const confirmar = async () => {
    if (!motivo.trim()) { toast.error('El motivo es obligatorio'); return; }
    if (!window.confirm(
      `¿Confirmas suspender esta salida?\n\n` +
      `${reservasCount?.total || 0} reservas se marcarán como suspendidas.\n` +
      `Se emitirán créditos por el monto pagado.\n\n` +
      `Esta acción NO se puede deshacer.`
    )) return;

    setEnviando(true);
    try {
      let userId = null;
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); userId = u.id || u.user_id || null; } catch {}
      const r = await fetch(`${API_BASE}/turismo/salidas/${salida.id}/suspender`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tipo, motivo: motivo.trim(),
          dias_vigencia_credito: parseInt(diasVigencia) || 365,
          user_id: userId,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'Error suspendiendo');
        setEnviando(false);
        return;
      }
      setResultado(data);
      onSuspendida?.(data);
    } catch (e) {
      toast.error('Error de red');
    }
    setEnviando(false);
  };

  const descargarCSV = () => {
    if (!resultado?.afectados?.length) return;
    const headers = ['Codigo reserva', 'Cliente', 'WhatsApp', 'Correo', 'Pax', 'Precio total', 'Monto pagado (credito)', 'Agencia'];
    const esc = v => v == null ? '' : (String(v).includes(',') || String(v).includes('"') || String(v).includes('\n')) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const rows = resultado.afectados.map(a => [
      a.codigo_reserva, a.nombre_cliente, a.numero_whatsapp, a.correo,
      a.total_pax, a.precio_total, a.monto_pagado, a.agencia_nombre || '',
    ].map(esc).join(','));
    const csv = '﻿' + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `salida-${salida.id}-suspendida-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const fechaSalida = salida.fecha ? new Date(salida.fecha).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';

  // Pantalla de resultado
  if (resultado) {
    return (
      <SlideOver open onClose={onClose}
        title="Salida suspendida"
        subtitle={resultado.paquete}
        icon={<CheckCircleIcon />}
        footer={
          <>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={descargarCSV}
              disabled={!resultado.afectados?.length}
              sx={{ textTransform: 'none', color: '#0f766e', borderColor: '#0f766e' }}>
              Descargar CSV ({resultado.afectados?.length || 0})
            </Button>
            <Button variant="contained" onClick={onClose} disableElevation
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              Cerrar
            </Button>
          </>
        }>
        <Stack spacing={2}>
          <Alert severity="success" sx={{ fontSize: 13 }}>
            La salida fue suspendida exitosamente.
          </Alert>

          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
            <Typography sx={{ fontSize: 12, color: '#0d9488', fontWeight: 700, textTransform: 'uppercase' }}>
              Resumen
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Row label="Reservas afectadas" value={resultado.reservas_afectadas} />
              <Row label="Créditos emitidos" value={resultado.creditos_emitidos} />
              <Row label="Monto total a crédito" value={`$${new Intl.NumberFormat('es-CO').format(resultado.monto_total_creditos)}`} />
              {resultado.fecha_vencimiento_credito && (
                <Row label="Vence el" value={new Date(resultado.fecha_vencimiento_credito).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })} />
              )}
            </Stack>
          </Box>

          <Alert severity="warning" sx={{ fontSize: 12 }}>
            <b>Importante:</b> el sistema NO envió WhatsApp ni correo automáticamente.
            Descarga el CSV y contacta a cada cliente manualmente para informar la suspensión
            y el crédito a su favor.
          </Alert>

          <Box>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
              Clientes afectados ({resultado.afectados?.length || 0})
            </Typography>
            <Stack spacing={0.5}>
              {(resultado.afectados || []).map(a => (
                <Paper key={a.reservacion_id} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 150 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                        {a.nombre_cliente} <Typography component="span" sx={{ fontSize: 10, fontFamily: 'monospace', color: '#64748b' }}>· {a.codigo_reserva}</Typography>
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        📱 {a.numero_whatsapp || '—'} · {a.total_pax} pax
                        {a.agencia_nombre && ` · 🏢 ${a.agencia_nombre}`}
                      </Typography>
                    </Box>
                    {a.credito_id && (
                      <Chip size="small"
                        label={`Crédito $${new Intl.NumberFormat('es-CO').format(a.monto_pagado)}`}
                        sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 10 }} />
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Stack>
      </SlideOver>
    );
  }

  // Pantalla de formulario
  return (
    <>
      <SlideOver open onClose={onClose}
        title="Suspender salida"
        subtitle={`${fechaSalida}`}
        icon={<EventBusyIcon />}
        footer={
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
            <Button variant="contained" onClick={confirmar} disabled={enviando || !motivo.trim()} disableElevation
              startIcon={<WarningAmberIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)' },
              }}>
              {enviando ? 'Suspendiendo…' : 'Confirmar suspensión'}
            </Button>
          </>
        }>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            Esta acción suspenderá la salida completa: marcará TODAS las reservas activas
            como suspendidas y creará créditos a favor de los clientes que ya pagaron.
            <b> Esta acción no es reversible.</b>
          </Alert>

          {/* Info de la salida */}
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <Typography sx={{ fontSize: 10, color: '#991b1b', fontWeight: 700, textTransform: 'uppercase' }}>
              Salida a suspender
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 0.5 }}>
              {salida.paquete_nombre || salida.paquete?.nombre || 'Salida'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
              {fechaSalida} · {salida.hora_salida?.slice(0, 5) || '—'}
            </Typography>
          </Box>

          {/* Reservas afectadas */}
          {reservasCount === null ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <CircularProgress size={20} sx={{ color: '#dc2626' }} />
            </Box>
          ) : reservasCount.total === 0 ? (
            <Alert severity="info" sx={{ fontSize: 12 }}>
              No hay reservas activas para esta salida. La suspensión solo cambia el estado de la salida.
            </Alert>
          ) : (
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
              <Typography sx={{ fontSize: 10, color: '#92400e', fontWeight: 700, textTransform: 'uppercase' }}>
                Impacto
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Row label="Reservas afectadas" value={reservasCount.total} />
                <Row label="Pasajeros" value={reservasCount.totalPax} />
                <Row label="Total créditos a emitir" value={`$${new Intl.NumberFormat('es-CO').format(reservasCount.totalMonto)}`} />
              </Stack>
            </Box>
          )}

          <Divider />

          {/* Tipo */}
          <TextField size="small" select label="Tipo de suspensión *" value={tipo}
            onChange={e => setTipo(e.target.value)}
            helperText={TIPOS.find(t => t.v === tipo)?.help}>
            {TIPOS.map(t => (
              <MenuItem key={t.v} value={t.v}>{t.label}</MenuItem>
            ))}
          </TextField>

          {/* Motivo */}
          <TextField size="small" multiline minRows={3} label="Motivo detallado *"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder={
              tipo === 'clima' ? 'Ej: DIMAR emitió alerta amarilla por mal tiempo. Vientos superiores a 25 nudos.' :
              tipo === 'dimar' ? 'Ej: DIMAR ordenó suspensión por inspección técnica de la embarcación.' :
              tipo === 'operativo' ? 'Ej: Falla en el motor principal del Cogela Suave. En reparación.' :
              'Describe el motivo de la suspensión…'
            }
            required />

          {/* Vigencia del crédito */}
          <TextField size="small" type="number" label="Vigencia del crédito (días)"
            value={diasVigencia}
            onChange={e => setDiasVigencia(e.target.value)}
            helperText="Tiempo que el cliente tiene para redimir el crédito en otra reserva (default 365 días = 1 año)" />
        </Stack>
      </SlideOver>
      <ToastContainer />
    </>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{value}</Typography>
    </Box>
  );
}
